import { call } from '@advanza/api'
import { Button, Icon } from '@advanza/ui'
import { formatMoney } from 'misc/money'
import { useState } from 'react'

interface TroyCaseInvoice {
    invoiceId: number
    invoiceNr: string
    invoiceNo: string
    principal: number
    atTroy: boolean
    paid: boolean
}

interface TroyCaseStatus {
    caseId: string
    activeState: string | null
    stage: string | null
    invoices: TroyCaseInvoice[]
    principal: number
    creditNotes: number
    paid: number
    paidToTroy: number
    paidToUs: number
    troyOwnClaim: number
    outstanding: number
    hasClaim: boolean
    complete: boolean
    settled: boolean
    partiallyPaid: boolean
    caseItems: number
}

interface Props {
    caseId: string
    /** Any invoice on the case; the Office endpoints are addressed by invoice. */
    invoiceId: number
    invoiceCount: number
    stopped: boolean
    /** Called after Troy accepted a change, so the surrounding list can reload. */
    onChanged: () => void
}

type ApiError = { error?: string }

const errorMessage = (e: unknown, fallback: string) => (e as ApiError)?.error ?? fallback

const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`

const StatusLine = ({ status }: { status: TroyCaseStatus }) => {
    if (status.activeState === 'INACTIVE' && !status.hasClaim) {
        return (
            <p className="m-0 text-[12px] text-[#6b7280]">
                Case {status.caseId} is inactive at Troy. Troy hides inactive cases, so its balance
                cannot be read until collection is resumed.
            </p>
        )
    }

    if (!status.hasClaim) {
        return (
            <p className="m-0 text-[12px] text-[#c0392b]">
                Troy holds no claim line on case {status.caseId}; nothing can be reconciled against
                it.
            </p>
        )
    }

    const missing = status.invoices.filter((invoice) => !invoice.atTroy)

    const verdict = (() => {
        if (!status.complete) {
            return {
                text: `Troy holds no line for ${missing.map((invoice) => invoice.invoiceNr).join(', ')}; the case cannot be settled until it does`,
                color: '#c0392b',
            }
        }
        if (status.settled) {
            return {
                text: `Settled: ${plural(status.invoices.length, 'invoice')} covered`,
                color: '#1e7a4b',
            }
        }
        if (status.partiallyPaid) {
            return {
                text: `Partially paid: ${formatMoney(status.outstanding)} still open on the case`,
                color: '#8a6100',
            }
        }
        if (status.paid > 0) {
            return { text: "Paid so far went to Troy's own costs only", color: '#8a6100' }
        }
        return { text: 'No payment registered at Troy yet', color: '#6b7280' }
    })()

    return (
        <div className="text-[12px] text-[#4b5563]">
            <span className="font-semibold" style={{ color: verdict.color }}>
                {verdict.text}
            </span>
            <span className="mx-1">·</span>
            principal {formatMoney(status.principal)}, debtor paid {formatMoney(status.paid)} (
            {formatMoney(status.paidToTroy)} to Troy, {formatMoney(status.paidToUs)} to us)
            {status.activeState && (
                <>
                    <span className="mx-1">·</span>
                    {status.activeState}
                    {status.stage ? ` / ${status.stage}` : ''}
                </>
            )}
            {status.caseItems !== status.invoices.length && (
                <>
                    <span className="mx-1">·</span>
                    <span className="text-[#8a6100]">
                        Troy holds {plural(status.caseItems, 'claim line')} for{' '}
                        {plural(status.invoices.length, 'invoice')} of ours
                    </span>
                </>
            )}
        </div>
    )
}

/**
 * What a person can do with a Troy case: read what Troy has collected and mark the invoices
 * paid on that basis, or tell Troy to stop because the debt was settled outside Troy.
 *
 * Everything here is per case, not per invoice. A debtor's invoices share one case, Troy
 * reports payments per case without saying which invoice they were for, and its active
 * state is the case's. So a settlement marks every invoice on the case paid at once, and a
 * stop silences all of them.
 */
const TroyCaseActions = ({ caseId, invoiceId, invoiceCount, stopped, onChanged }: Props) => {
    const [status, setStatus] = useState<TroyCaseStatus | null>(null)
    const [busy, setBusy] = useState<'status' | 'settle' | 'state' | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [notice, setNotice] = useState<string | null>(null)
    /** Which state change the reason box is for; the same box serves both directions. */
    const [stateChange, setStateChange] = useState<'stop' | 'resume' | null>(null)
    const [reason, setReason] = useState('')

    const checkStatus = async () => {
        setBusy('status')
        setError(null)
        setNotice(null)
        try {
            const response = await call(`office/invoices/troy-case-status/${invoiceId}`)
            setStatus(response.status)
        } catch (e: unknown) {
            setError(errorMessage(e, 'Could not read the case from Troy'))
        } finally {
            setBusy(null)
        }
    }

    const settle = async () => {
        setBusy('settle')
        setError(null)
        try {
            const response = await call(`office/invoices/troy-settle/${invoiceId}`, { json: {} })
            if (response.result === 'settled') {
                const settled: string[] = response.invoiceNrs ?? []
                setNotice(
                    settled.length === 1
                        ? `Invoice ${settled[0]} is marked paid from Troy's balance.`
                        : `${plural(settled.length, 'invoice')} marked paid from Troy's balance: ${settled.join(', ')}.`,
                )
                onChanged()
                return
            }
            if (response.status) {
                setStatus(response.status)
            }
            setNotice('Troy does not show this case as settled yet, nothing was changed.')
        } catch (e: unknown) {
            setError(errorMessage(e, 'Could not settle from Troy'))
        } finally {
            setBusy(null)
        }
    }

    const changeState = async () => {
        if (stateChange === null) {
            return
        }
        setBusy('state')
        setError(null)
        try {
            const response = await call(`office/invoices/troy-${stateChange}/${invoiceId}`, {
                json: { reason },
            })
            const accepted =
                stateChange === 'stop' ? ['stopped', 'already_stopped'] : ['resumed', 'not_stopped']
            if (accepted.includes(response.result)) {
                const what = invoiceCount === 1 ? 'this invoice' : `these ${invoiceCount} invoices`
                setNotice(
                    stateChange === 'stop'
                        ? `Troy has been told to stop collecting ${what}.`
                        : `Troy has been told to resume collecting ${what}.`,
                )
                setStateChange(null)
                setReason('')
                setStatus(null)
                onChanged()
                return
            }
            setError(`Unexpected answer: ${response.result}`)
        } catch (e: unknown) {
            setError(errorMessage(e, 'Troy did not accept the change'))
        } finally {
            setBusy(null)
        }
    }

    return (
        <div className="mt-2 flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2">
                <Button
                    appearance="secondary"
                    iconBefore="sync"
                    disabled={busy !== null}
                    onClick={checkStatus}>
                    {busy === 'status' ? 'Reading…' : 'Check Troy'}
                </Button>
                {status?.settled && (
                    <Button
                        appearance="primary"
                        iconBefore="paid"
                        disabled={busy !== null}
                        onClick={settle}>
                        {busy === 'settle' ? 'Marking paid…' : 'Mark paid from Troy'}
                    </Button>
                )}
                {!stopped && stateChange === null && (
                    <Button
                        appearance="secondary"
                        iconBefore="pause_circle"
                        disabled={busy !== null}
                        onClick={() => setStateChange('stop')}>
                        Stop collection
                    </Button>
                )}
                {stopped && stateChange === null && (
                    <Button
                        appearance="secondary"
                        iconBefore="play_circle"
                        disabled={busy !== null}
                        onClick={() => setStateChange('resume')}>
                        Resume collection
                    </Button>
                )}
            </div>

            {status && <StatusLine status={status} />}

            {stateChange !== null && (
                <div className="rounded-[6px] border border-[#fde68a] bg-[#fffbeb] p-2">
                    <p className="m-0 mb-1 text-[12px] text-[#8a6100]">
                        {(() => {
                            if (stateChange === 'resume') {
                                return 'Troy starts chasing the debtor again. Use this when a stop was a mistake or the private arrangement fell through. The reason is sent to Troy.'
                            }
                            if (invoiceCount > 1) {
                                return `Troy stops chasing the debtor for all ${invoiceCount} invoices on case ${caseId}; it cannot stop one of them. Use this only when the provider has paid everything directly or finance settled it another way. The reason is sent to Troy.`
                            }
                            return 'Troy stops chasing the debtor for this invoice. Use this when the provider paid us directly or finance settled it another way. The reason is sent to Troy.'
                        })()}
                    </p>
                    <textarea
                        className="w-full rounded-[4px] border border-[#e5e7eb] p-2 text-[13px]"
                        rows={2}
                        placeholder={
                            stateChange === 'stop'
                                ? 'Reason, e.g. paid directly by bank transfer on 2026-09-07'
                                : 'Reason, e.g. stopped by mistake, provider has not paid'
                        }
                        value={reason}
                        onChange={(event) => setReason(event.target.value)}
                    />
                    <div className="mt-1 flex items-center justify-end gap-2">
                        <Button
                            appearance="secondary"
                            disabled={busy !== null}
                            onClick={() => setStateChange(null)}>
                            Cancel
                        </Button>
                        <Button
                            appearance="primary"
                            iconBefore={stateChange === 'stop' ? 'pause_circle' : 'play_circle'}
                            disabled={busy !== null || reason.trim() === ''}
                            onClick={changeState}>
                            {(() => {
                                if (busy === 'state') {
                                    return 'Sending…'
                                }
                                return stateChange === 'stop' ? 'Confirm stop' : 'Confirm resume'
                            })()}
                        </Button>
                    </div>
                </div>
            )}

            {notice && (
                <p className="m-0 flex items-center gap-1 text-[12px] text-[#1e7a4b]">
                    <Icon name="check_circle" fontSize={14} className="inherit" />
                    {notice}
                </p>
            )}
            {error && (
                <p className="m-0 flex items-center gap-1 text-[12px] text-[#c0392b]">
                    <Icon name="error" fontSize={14} className="inherit" />
                    {error}
                </p>
            )}
        </div>
    )
}

export default TroyCaseActions
